import { GlSkeletonLoader, GlTooltipDirective } from '@gitlab/ui';
import { uniqueId, orderBy } from 'lodash-es';
import ApprovalCheckRulePopover from 'ee/approvals/components/mr_widget_approval_check/approval_check_rule_popover.vue';
import EmptyRuleApprovers from 'ee/approvals/components/rules/empty_rule_approvers.vue';
import { RULE_TYPE_CODE_OWNER, RULE_TYPE_ANY_APPROVER } from 'ee/approvals/constants';
import { sprintf, __, s__ } from '~/locale';
import UserAvatarList from '~/vue_shared/components/user_avatar/user_avatar_list.vue';
import { TYPENAME_MERGE_REQUEST } from '~/graphql_shared/constants';
import { convertToGraphQLId } from '~/graphql_shared/utils';
import ApprovedIcon from './approved_icon.vue';
import NumberOfApprovals from './number_of_approvals.vue';
import ApprovalsUsersList from './approvals_users_list.vue';
import approvalRulesQuery from './queries/approval_rules.query.graphql';
import mergeRequestApprovalStateUpdated from './queries/approval_rules.subscription.graphql';
import { createSecurityPolicyRuleHelpText } from './utils';
const INCLUDE_APPROVERS = 1;
const DO_NOT_INCLUDE_APPROVERS = 2;
export default {
    name: 'ApprovalsList',
    apollo: {
        mergeRequest: {
            query: approvalRulesQuery,
            variables () {
                return {
                    projectPath: this.projectPath,
                    iid: this.iid
                };
            },
            update: (data)=>data?.project?.mergeRequest,
            subscribeToMore: {
                document: mergeRequestApprovalStateUpdated,
                variables () {
                    return {
                        issuableId: convertToGraphQLId(TYPENAME_MERGE_REQUEST, this.mergeRequest.id)
                    };
                },
                skip () {
                    return !this.mergeRequest?.id;
                },
                updateQuery (_, { subscriptionData: { data: { mergeRequestApprovalStateUpdated: queryResult } } }) {
                    if (queryResult) {
                        this.mergeRequest = queryResult;
                    }
                }
            }
        }
    },
    components: {
        GlSkeletonLoader,
        UserAvatarList,
        ApprovedIcon,
        ApprovalCheckRulePopover,
        EmptyRuleApprovers,
        NumberOfApprovals,
        ApprovalsUsersList
    },
    directives: {
        GlTooltip: GlTooltipDirective
    },
    props: {
        projectPath: {
            type: String,
            required: true
        },
        iid: {
            type: String,
            required: true
        },
        codeCoverageCheckHelpPagePath: {
            type: String,
            required: false,
            default: ''
        },
        eligibleApproversDocsPath: {
            type: String,
            required: false,
            default: ''
        }
    },
    data () {
        return {
            mergeRequest: null
        };
    },
    computed: {
        sections () {
            const approvalRules = this.mergeRequest.approvalState.rules;
            return [
                {
                    id: uniqueId(),
                    title: '',
                    rules: approvalRules.filter((rule)=>rule.type.toLowerCase() !== RULE_TYPE_CODE_OWNER)
                },
                {
                    id: uniqueId(),
                    title: __('Code Owners'),
                    rules: orderBy(approvalRules.filter((rule)=>rule.type.toLowerCase() === RULE_TYPE_CODE_OWNER).map((rule)=>({
                            ...rule,
                            nameClass: 'gl-font-monospace gl-break-all'
                        })), [
                        (o)=>o.section === 'codeowners',
                        'name',
                        'section'
                    ], [
                        'desc',
                        'asc',
                        'asc'
                    ])
                }
            ].filter((x)=>x.rules.length);
        }
    },
    methods: {
        getTooltipText (rule) {
            if (!rule?.scanResultPolicies) return '';
            return createSecurityPolicyRuleHelpText(rule.scanResultPolicies);
        },
        summaryText (rule) {
            return rule.approvalsRequired === 0 ? this.summaryOptionalText(rule) : this.summaryRequiredText(rule);
        },
        summaryRequiredText (rule) {
            return sprintf(__('%{count} of %{required} approvals from %{name}'), {
                count: rule.approvedBy.nodes.length,
                required: rule.approvalsRequired,
                name: rule.name
            });
        },
        summaryOptionalText (rule) {
            return sprintf(__('%{count} approvals from %{name}'), {
                count: rule.approvedBy.nodes.length,
                name: rule.name
            });
        },
        groupedPatterns (rule) {
            return rule.name === rule.section ? rule.patterns : null;
        },
        sectionNameLabel (rule) {
            return sprintf(s__('Approvals|Section: %{section}'), {
                section: rule.section
            });
        },
        numberOfColumns (rule) {
            return rule.type.toLowerCase() === this.$options.ruleTypeAnyApprover ? DO_NOT_INCLUDE_APPROVERS : INCLUDE_APPROVERS;
        }
    },
    i18n: {
        commentedBy: s__('MRApprovals|Commented by'),
        approvedBy: s__('MRApprovals|Approved by')
    },
    ruleTypeAnyApprover: RULE_TYPE_ANY_APPROVER
};
